<script setup lang="ts">
import {computed} from 'vue'
import {useRoute} from 'vue-router'
import {TooltipProvider} from '@/components/ui/tooltip/index'
import {Toaster} from '@/components/ui/sonner/index'
import {provideBreadcrumbs} from "@/composables/useBreadcrumbs";
import Header from "@/components/www/layout/Header.vue";
import Footer from "@/components/www/layout/Footer.vue";
import LandingHeader from "@/components/www/layout/LandingHeader.vue";
import LandingFooter from "@/components/www/layout/LandingFooter.vue";
import CookieBanner from "@/components/www/layout/CookieBanner.vue";
import {isLandingChrome, landingCtaFromMeta} from "@/components/www/layout/landingChrome";
import {useHead, useSeoMeta} from "@unhead/vue";
import {wwwUrlTo} from "@/lib/urlTo";
import {buildWwwSiteJsonLd} from '@/lib/wwwSiteJsonLd';
import {useLenis} from "@/composables/www/useLenis";
import {applyTheme} from "@/lib/theme";
import '@/entrypoints/www/marketing.css'

provideBreadcrumbs()

// Marketing site is single-theme for now (light). The ThemeSwitch + www_theme
// cookie were removed with the redesign — dark mode returns in a follow-up.
applyTheme('light')

useLenis()

defineOptions({name: 'WwwApp'})

const route = useRoute()
// Pages like /api-docs render an internal sticky sidebar (Scalar) and need a
// static in-flow header instead of the fixed marketing chrome.
const isFullBleed = computed<boolean>(() => route.path.startsWith('/api-docs'))
const headerTheme = computed<'light' | 'dark'>(() => (route.meta.headerTheme === 'dark' ? 'dark' : 'light'))
const landingChrome = computed(() => isLandingChrome(route.meta))
const landingCta = computed(() => landingCtaFromMeta(route.meta))

const title = 'Automated fashion marketplace listing for Shopify | Osello';
const description = 'Create and optimize Macy\'s, Nordstrom, Target and other marketplace fashion & apparel listings automatically with the power of AI agents.';
// og:title / og:description / twitter:card are inferred per page from the resolved title +
// meta description by InferSeoMetaPlugin (setupSpa.ts + ssr/www.ts) — don't set them here or
// every page would share one static preview again.
useSeoMeta({
    title,
    description,
    author: 'Osello',
    colorScheme: 'light',
    themeColor: '#007060',
    applicationName: 'Osello',
    ogImage: 'https://open-cdn.osello.com/brand/banner/meta.png',
    ogImageAlt: 'Painless marketplace listing for fashion brands with Osello.',
    ogImageWidth: 2400,
    ogImageHeight: 1260,
    ogUrl: () => wwwUrlTo(route.path),
    ogSiteName: 'Osello',
    ogLocale: 'en_US',
    twitterSite: '@osello_com',
    twitterCreator: '@osello_com',
    twitterImage: 'https://open-cdn.osello.com/brand/banner/meta.png',
    robots: 'index, follow, max-image-preview:large',
})

const siteJsonLd = computed(() => buildWwwSiteJsonLd({
    includeSoftwareApplication: route.path === '/' || route.path === '/pricing',
}))

useHead({
    script: computed(() => [{type: 'application/ld+json', innerHTML: siteJsonLd.value}]),
})
</script>

<template>
  <TooltipProvider>
    <a class="skip-link" href="#main-content">Skip to content</a>
    <LandingHeader
        v-if="landingChrome"
        :theme="headerTheme"
        :logoTo="typeof route.meta.landingLogoTo === 'string' ? route.meta.landingLogoTo : route.path"
        :logoLabel="typeof route.meta.landingLogoLabel === 'string' ? route.meta.landingLogoLabel : 'Back to top'"
        :logoScrollsToTop="typeof route.meta.landingLogoTo !== 'string'"
        :ctaLabel="landingCta.label"
        :ctaTo="landingCta.to"
        :ctaId="typeof route.meta.landingCtaId === 'string' ? route.meta.landingCtaId : undefined"
        :slimWhenScrolled="route.meta.landingHeaderSlim === true"
    />
    <Header
        v-else
        :theme="headerTheme"
        :static="isFullBleed"
        :compact="route.meta.headerCompact === true"
        :currentPath="route.fullPath"
    />
    <main
        id="main-content"
        class="www ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        :class="isFullBleed ? 'overflow-visible' : 'www-main'"
    >
      <router-view/>
    </main>
    <LandingFooter v-if="landingChrome"/>
    <Footer v-else/>
    <CookieBanner/>
    <Toaster :duration="4000" position="top-right" rich-colors/>
  </TooltipProvider>
</template>

<style>
/* Clip sideways overflow from full-bleed animated sections without creating a
   scroll container (keeps position: sticky working inside). */
.www-main {
    overflow-x: clip;
}
</style>
